import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, Facebook, Instagram, Briefcase } from 'lucide-react';
import FadeIn from './FadeIn';
import Card from './Card';

export default function Contact() {
  const { t } = useTranslation('home');

  // Email link helper (obfuscation handled by mailto: protocol)
  const getEmailHref = (email: string) => {
    return `mailto:${email}`;
  };

  const emailContacts = [
    { label: 'General', email: t('contact.email.general'), icon: Mail },
    { label: 'Support', email: t('contact.email.support'), icon: MessageCircle },
    { label: 'Business', email: t('contact.email.business'), icon: Briefcase },
    { label: 'Press', email: t('contact.email.press'), icon: Mail },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/suersdey', color: 'hover:text-blue-600' },
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/suersdey/', color: 'hover:text-pink-600' },
  ];

  return (
    <section id="contact" className="section-padding bg-white">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <FadeIn>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-neutral-900 mb-4">
                {t('contact.title')}
              </h2>
              <p className="text-xl bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent font-semibold">
                {t('contact.subtitle')}
              </p>
            </div>
          </FadeIn>

          {/* Email Contacts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {emailContacts.map((contact, index) => {
              const Icon = contact.icon;
              const gradients = [
                'from-primary-500 to-primary-600',
                'from-secondary-500 to-secondary-600',
                'from-accent-500 to-accent-600',
                'from-primary-600 to-secondary-600',
              ];
              return (
                <FadeIn key={contact.label} delay={100 + index * 100}>
                  <a
                    href={getEmailHref(contact.email)}
                    className="block group"
                  >
                    <Card hover className="transition-all border border-primary-100 group-hover:border-primary-300 group-hover:shadow-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 bg-gradient-to-br ${gradients[index]} rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                          <Icon size={24} className="text-white" />
                        </div>
                        <div>
                          <div className="text-sm text-neutral-500 font-medium">{contact.label}</div>
                          <div className="text-neutral-900 font-semibold group-hover:text-primary-600 transition-colors">{contact.email}</div>
                        </div>
                      </div>
                    </Card>
                  </a>
                </FadeIn>
              );
            })}
          </div>

          {/* Social Links */}
          <FadeIn delay={500}>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-neutral-900 mb-6">Follow Us</h3>
            <div className="flex justify-center gap-6 mb-8">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-16 h-16 bg-gradient-to-br from-primary-100 to-accent-100 rounded-full flex items-center justify-center text-primary-600 ${social.color} transition-all shadow-md hover:shadow-xl transform hover:scale-110 border border-primary-200 hover:border-primary-300`}
                    aria-label={social.name}
                  >
                    <Icon size={28} />
                  </a>
                );
              })}
            </div>

              {/* CTA Text */}
              <p className="text-neutral-600">
                {t('contact.cta')}
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
